// @flow

import { pinParticipant } from '../base/participants/actions';
import {
    getFakeParticipants,
    getParticipantById,
    getPinnedParticipant,
    isLocalScreenshareParticipant,
    isSharedVideoParticipant
} from '../base/participants/functions';
import { equals } from '../base/redux';
import { isParticipantIdAllowedInFocusMode } from '../focus-mode/functions';
import { isFollowMeActive } from '../follow-me';
import { selectParticipantInLargeVideo } from '../large-video/actions.any';

import { setPriorityContentStack } from './actions';
import { LAYOUTS } from './constants';
import { getCurrentLayout } from './functions';

/**
 * The set of presentation content types tracked by the unified LIFO priority
 * content stack. Screen share and shared video are pinnable participant tiles;
 * whiteboard and etherpad are full-screen overlays that are tracked only for
 * stack ordering (they are not pinned to the large video).
 */
export const PRIORITY_CONTENT_TYPES = {
    SCREENSHARE: 'screenshare',
    SHAREDVIDEO: 'sharedvideo',
    WHITEBOARD: 'whiteboard',
    ETHERPAD: 'etherpad'
};

/**
 * The presentation content types that can be pinned to the large video (i.e. that
 * are represented by a participant tile).
 */
const PINNABLE_TYPES = new Set([
    PRIORITY_CONTENT_TYPES.SCREENSHARE,
    PRIORITY_CONTENT_TYPES.SHAREDVIDEO
]);

// Stable synthetic ids for the overlay presentation types (they are not tiles).
const WHITEBOARD_ITEM_ID = 'presentation:whiteboard';
const ETHERPAD_ITEM_ID = 'presentation:etherpad';

/**
 * Returns the list of currently active presentation items derived from redux state.
 * Screen shares and shared video are pinnable participant tiles; whiteboard and
 * etherpad are full-screen overlays tracked only for stack ordering. The cross-type
 * order is stabilized by {@link reconcilePriorityStack}.
 *
 * @param {Object} state - The redux state.
 * @returns {Array<{ id: string, type: string }>}
 */
export function getActivePresentationItems(state: Object) {
    const items = [];
    const fakeParticipants = getFakeParticipants(state) || [];

    // Screen shares (local + remote). We source these from `remoteScreenShares`, which
    // is already filtered/ordered by the auto-pin setting (AUTO_PIN_LATEST_SCREEN_SHARE
    // and "remote-only") in the tracks subscriber, so the most recent allowed sharer
    // ends up on top of the stack consistently.
    const remoteOrder = state['features/video-layout']?.remoteScreenShares || [];

    remoteOrder.forEach(id => {
        if (getParticipantById(state, id) && isParticipantIdAllowedInFocusMode(state, id)) {
            items.push({
                id,
                type: PRIORITY_CONTENT_TYPES.SCREENSHARE
            });
        }
    });

    // Local screen share. The "remote-only" auto-pin setting intentionally keeps the
    // local screen share out of `remoteScreenShares`, but the local sharer still needs
    // their own share pinned to the large video, so we add its fake participant here
    // (it is deduped by id in reconcilePriorityStack).
    const localScreenShare = fakeParticipants.find(p => isLocalScreenshareParticipant(p));

    if (localScreenShare) {
        items.push({
            id: localScreenShare.id,
            type: PRIORITY_CONTENT_TYPES.SCREENSHARE
        });
    }

    // Shared video (YouTube / video player).
    const sharedVideo = fakeParticipants.find(p => isSharedVideoParticipant(p));

    if (sharedVideo && isParticipantIdAllowedInFocusMode(state, sharedVideo.id)) {
        items.push({
            id: sharedVideo.id,
            type: PRIORITY_CONTENT_TYPES.SHAREDVIDEO
        });
    }

    // Whiteboard overlay.
    if (state['features/genericiframe']?.visible) {
        items.push({
            id: WHITEBOARD_ITEM_ID,
            type: PRIORITY_CONTENT_TYPES.WHITEBOARD
        });
    }

    // Etherpad overlay.
    if (state['features/etherpad']?.editing) {
        items.push({
            id: ETHERPAD_ITEM_ID,
            type: PRIORITY_CONTENT_TYPES.ETHERPAD
        });
    }

    return items;
}

/**
 * A cheap, deterministic signature of the currently active presentation items. Used
 * as a {@code StateListenerRegistry} selector so the orchestrator only runs when the
 * set/order of active presentations actually changes (and not on unrelated pin state
 * changes it dispatches itself).
 *
 * @param {Object} state - The redux state.
 * @returns {string}
 */
export function getActivePresentationSignature(state: Object) {
    return getActivePresentationItems(state)
        .map(item => `${item.type}:${item.id}`)
        .join('|');
}

/**
 * Reconciles the previous stack with the currently active items, preserving the order
 * (and timestamps) of items that are still active and appending newly active items to
 * the top (LIFO push). Items that are no longer active are dropped (pop).
 *
 * @param {Array<Object>} oldStack - The previous priority content stack.
 * @param {Array<Object>} activeItems - The currently active presentation items.
 * @returns {Array<{ id: string, type: string, timestamp: number }>}
 */
export function reconcilePriorityStack(oldStack: Array<Object> = [], activeItems: Array<Object> = []) {
    const activeById = new Map(activeItems.map(item => [ item.id, item ]));
    const kept = oldStack.filter(item => activeById.has(item.id));
    const keptIds = new Set(kept.map(item => item.id));
    const now = Date.now();
    const added = activeItems
        .filter(item => !keptIds.has(item.id))
        .map(item => {
            return {
                id: item.id,
                type: item.type,
                timestamp: now
            };
        });

    return [ ...kept, ...added ];
}

/**
 * Returns the id of the topmost pinnable presentation (screen share or shared video)
 * in the given stack whose participant still exists in state, or {@code null} if none.
 *
 * @param {Array<Object>} stack - The priority content stack.
 * @param {Object} state - The redux state.
 * @returns {?string}
 */
export function getTopPinnablePresentationId(stack: Array<Object> = [], state: Object) {
    for (let i = stack.length - 1; i >= 0; i--) {
        const item = stack[i];

        if (PINNABLE_TYPES.has(item.type)
                && getParticipantById(state, item.id)
                && isParticipantIdAllowedInFocusMode(state, item.id)) {
            return item.id;
        }
    }

    return null;
}

/**
 * The unified Layout & Priority Orchestrator. Reconciles the LIFO priority content
 * stack from the current state, then coordinates pinning and large-video election:
 *
 *  - Newest presentation wins: the topmost pinnable item is auto-pinned.
 *  - Ordered fallback on stop: when the active presentation ends, the previous
 *    pinnable item on the stack is pinned; if none remain, the auto-pin is cleared.
 *  - A manual (user) pin or a moderator pin always takes precedence and is never
 *    overridden.
 *  - Auto-pinning is suppressed in tile view and follow-me mode.
 *
 * This is intentionally platform-agnostic so the exact same logic runs on Web and
 * React Native.
 *
 * @param {Object} store - The redux store ({@code { dispatch, getState }}).
 * @returns {void}
 */
export function orchestratePriorityContent(store: Object) {
    const { dispatch, getState } = store;
    const state = getState();

    // Step A: update the unified stack (declarative reconcile -> push/pop).
    const oldStack = state['features/video-layout']?.priorityContentStack || [];
    const newStack = reconcilePriorityStack(oldStack, getActivePresentationItems(state));

    if (!equals(oldStack, newStack)) {
        dispatch(setPriorityContentStack(newStack));
    }

    // Auto-pinning is disabled in tile view and follow-me mode.
    if (getCurrentLayout(state) === LAYOUTS.TILE_VIEW || isFollowMeActive(store)) {
        return;
    }

    const pinnedParticipant = getPinnedParticipant(state);
    const topPinnableId = getTopPinnablePresentationId(newStack, state);

    if (topPinnableId && (!pinnedParticipant || pinnedParticipant.isFakeParticipant)) {
        // Newest presentation wins.
        if (!pinnedParticipant || pinnedParticipant.id !== topPinnableId) {
            dispatch(pinParticipant(topPinnableId, true));
        }
    } else if (pinnedParticipant
            && (pinnedParticipant.isFakeParticipant
                || !isParticipantIdAllowedInFocusMode(state, pinnedParticipant.id))) {
        // The auto-pinned presentation ended and nothing pinnable remains, or Focus
        // Mode hid this pin from the local viewer (another guest's screenshare) ->
        // unpin so normal dominant-speaker logic resumes.
        dispatch(pinParticipant(null));
    }

    // Step D: elect the large-video participant using the 4-tier hierarchy.
    dispatch(selectParticipantInLargeVideo());
}

